import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Resource {
  id: string;
  title: string;
  description: string;
  category: string;
  type: string;
  file_url: string;
  thumbnail_url: string;
  download_count: number;
  is_premium: boolean;
  tags: string[];
  created_at: string;
}

const categories = [
  { id: 'all', name: 'All Resources', icon: 'ri-folder-line' },
  { id: 'housing', name: 'Housing', icon: 'ri-home-line' },
  { id: 'employment', name: 'Employment', icon: 'ri-briefcase-line' },
  { id: 'visa', name: 'Visa & Immigration', icon: 'ri-passport-line' },
  { id: 'healthcare', name: 'Healthcare', icon: 'ri-heart-pulse-line' },
  { id: 'finance', name: 'Finance', icon: 'ri-money-euro-circle-line' },
  { id: 'education', name: 'Education', icon: 'ri-book-line' },
  { id: 'culture', name: 'Culture', icon: 'ri-global-line' },
  { id: 'legal', name: 'Legal', icon: 'ri-scales-line' }
];

const resourceTypes = [
  { id: 'all', name: 'All Types', icon: 'ri-file-list-line' },
  { id: 'guide', name: 'Guides', icon: 'ri-book-open-line' },
  { id: 'template', name: 'Templates', icon: 'ri-file-text-line' },
  { id: 'video', name: 'Videos', icon: 'ri-video-line' },
  { id: 'checklist', name: 'Checklists', icon: 'ri-checkbox-line' }
];

export default function ResourcesPage() {
  const { user } = useAuth();
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadResources();
  }, [selectedCategory, selectedType]);

  const loadResources = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('resource_library')
        .select('*')
        .order('download_count', { ascending: false });

      if (selectedCategory !== 'all') {
        query = query.eq('category', selectedCategory);
      }

      if (selectedType !== 'all') {
        query = query.eq('type', selectedType);
      }

      const { data, error } = await query;
      if (error) throw error;
      setResources(data || []);
    } catch (error) {
      console.error('Error loading resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadResource = async (resource: Resource) => {
    try {
      // Track download
      if (user) {
        await supabase
          .from('resource_downloads')
          .insert([{
            resource_id: resource.id,
            user_id: user.id
          }]);

        // Update download count
        await supabase
          .from('resource_library')
          .update({ download_count: resource.download_count + 1 })
          .eq('id', resource.id);
      }

      // Open resource
      window.open(resource.file_url, '_blank');
      loadResources();
    } catch (error) {
      console.error('Error downloading resource:', error);
    }
  };

  const filteredResources = resources.filter(resource =>
    resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resource.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resource.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar scrolled={true} />
      
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-[#0A381F] mb-2">Resource Library</h1>
            <p className="text-gray-600">Downloadable guides, templates, and tools to help you settle in Ireland</p>
          </div>

          {/* Search */}
          <div className="mb-8">
            <div className="relative max-w-2xl">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search resources..."
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A381F]"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="mb-8 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Categories</h3>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                      selectedCategory === cat.id
                        ? 'bg-[#0A381F] text-white'
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <i className={`${cat.icon} mr-2`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Resource Type</h3>
              <div className="flex flex-wrap gap-2">
                {resourceTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedType(type.id)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                      selectedType === type.id
                        ? 'bg-[#0A381F] text-white'
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <i className={`${type.icon} mr-2`}></i>
                    {type.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Resources Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A381F]"></div>
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <i className="ri-folder-open-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No resources found</h3>
              <p className="text-gray-500">Try adjusting your filters or search query</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResources.map((resource) => (
                <div key={resource.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="relative h-48">
                    <img
                      src={resource.thumbnail_url || `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7Bresource.category%7D%20guide%20illustration%2C%20professional%20document%20design%2C%20clean%20and%20modern%20style%20with%20icons%20representing%20$%7Bresource.title%7D&width=800&height=600&seq=${resource.id}&orientation=landscape`}
                      alt={resource.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-4 right-4 flex gap-2">
                      <span className="px-3 py-1 bg-white/90 text-gray-700 text-xs font-semibold rounded-full">
                        {resource.type}
                      </span>
                      {resource.is_premium && (
                        <span className="px-3 py-1 bg-yellow-500 text-white text-xs font-semibold rounded-full">
                          <i className="ri-vip-crown-line mr-1"></i>
                          Premium
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{resource.title}</h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {resource.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {resource.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          #{tag}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <span>
                        <i className="ri-download-line mr-1"></i>
                        {resource.download_count} downloads
                      </span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full">
                        {resource.category}
                      </span>
                    </div>

                    <button
                      onClick={() => downloadResource(resource)}
                      className="w-full px-4 py-2 bg-[#0A381F] text-white rounded-lg font-medium hover:bg-[#0A381F]/90 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-download-cloud-line mr-2"></i>
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Popular Resources */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Most Popular Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Complete Guide to Renting in Ireland',
                  description: 'Everything you need to know about finding and renting accommodation in Ireland',
                  category: 'Housing',
                  downloads: 2847,
                  icon: 'ri-home-line'
                },
                {
                  title: 'Irish Job Application Template Pack',
                  description: 'Professional CV and cover letter templates tailored for the Irish job market',
                  category: 'Employment',
                  downloads: 2156,
                  icon: 'ri-briefcase-line'
                },
                {
                  title: 'PPS Number Application Checklist',
                  description: 'Step-by-step guide to applying for your Personal Public Service Number',
                  category: 'Legal',
                  downloads: 1923,
                  icon: 'ri-checkbox-line'
                },
                {
                  title: 'Healthcare System Guide',
                  description: 'Understanding the Irish healthcare system and how to access services',
                  category: 'Healthcare',
                  downloads: 1654,
                  icon: 'ri-heart-pulse-line'
                }
              ].map((item, index) => (
                <div key={index} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0A381F] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-white text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          <i className="ri-download-line mr-1"></i>
                          {item.downloads.toLocaleString()} downloads
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full">
                          {item.category}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
